import React from 'react';
import SEO from '../../components/SEO/SEO';

import { Link } from 'react-router-dom';
import './EmailMarketing.css';

const EmailMarketing = () => {
  const emailServices = [
    {
      id: 'strategy',
      icon: '📧',
      title: 'Email Campaign Strategy',
      description: 'Custom email marketing strategies tailored to your business goals and audience.',
      features: [
        'Audience Segmentation',
        'Content Calendar Planning',
        'A/B Testing Framework',
        'Performance Metrics & KPIs'
      ]
    },
    {
      id: 'automation',
      icon: '🤖',
      title: 'Marketing Automation',
      description: 'Automated email sequences that nurture leads and drive conversions.',
      features: [
        'Welcome Series Setup',
        'Drip Campaigns',
        'Behavioral Triggers',
        'Lead Nurturing Sequences'
      ]
    },
    {
      id: 'design',
      icon: '🎨',
      title: 'Email Template Design',
      description: 'Beautiful, responsive email templates that convert on all devices.',
      features: [
        'Mobile-Responsive Design',
        'Brand-Consistent Templates',
        'Interactive Elements',
        'Custom HTML/CSS'
      ]
    },
    {
      id: 'analytics',
      icon: '📊',
      title: 'Analytics & Optimization',
      description: 'Data-driven insights to improve open rates, clicks, and conversions.',
      features: [
        'Performance Tracking',
        'Conversion Attribution',
        'ROI Analysis',
        'Continuous Optimization'
      ]
    }
  ];

  return (
    <div className="email-marketing-page">
            <SEO 
                title="Email Marketing Services | Digi Network" 
                description="Learn more about our Email Marketing services to grow your business online with Digi Network." 
                canonical="/emailmarketing/emailmarketing" 
            />

      <section className="email-hero">
        <div className="email-hero-bg"></div>
        <div className="container">
          <div className="email-hero-content">
            <div className="label">EMAIL MARKETING</div>
            <h1>Email Marketing That Drives Results</h1>
            <p className="email-hero-description">
              Build lasting relationships with your customers through strategic email marketing. 
              We create personalized campaigns that deliver the right message at the right time 
              to maximize engagement and conversions.
            </p>
            <div className="email-hero-cta">
              <Link to="/contact" className="btn btn-primary">
                Get Started
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Link>
              <Link to="/case-studies" className="btn btn-outline">View Results</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="email-stats">
        <div className="container">
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-number">25%</div>
              <div className="stat-label">Average Open Rate</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">5x</div>
              <div className="stat-label">Higher ROI vs Other Channels</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">40%</div>
              <div className="stat-label">Average Click-Through Rate</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">$42</div>
              <div className="stat-label">Average Revenue per Email</div>
            </div>
          </div>
        </div>
      </section>

      <section className="email-services-grid-section">
        <div className="container">
          <div className="section-header">
            <div className="label">EMAIL SERVICES</div>
            <h2>Comprehensive Email Marketing Solutions</h2>
            <p>From strategy to execution, we handle every aspect of your email marketing campaigns.</p>
          </div>

          <div className="email-services-grid">
            {emailServices.map((service) => (
              <div key={service.id} className="email-service-card">
                <div className="email-service-icon">{service.icon}</div>
                <h3>{service.title}</h3>
                <p className="email-service-description">{service.description}</p>

                <ul className="email-service-features">
                  {service.features.map((feature, index) => (
                    <li key={index}>
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <circle cx="10" cy="10" r="10" fill="#0037FC" opacity="0.1"/>
                        <path d="M6 10L9 13L14 7" stroke="#0037FC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="email-process">
        <div className="container">
          <div className="section-header">
            <div className="label">OUR PROCESS</div>
            <h2>How We Build Email Campaigns</h2>
          </div>

          <div className="process-grid">
            <div className="process-step">
              <div className="process-number">01</div>
              <h3>Audience Analysis</h3>
              <p>Understand your audience segments, preferences, and behavior to create targeted campaigns.</p>
            </div>

            <div className="process-step">
              <div className="process-number">02</div>
              <h3>Strategy Development</h3>
              <p>Create a comprehensive email marketing strategy aligned with your business goals.</p>
            </div>

            <div className="process-step">
              <div className="process-number">03</div>
              <h3>Campaign Creation</h3>
              <p>Design beautiful emails and set up automation sequences that engage and convert.</p>
            </div>

            <div className="process-step">
              <div className="process-number">04</div>
              <h3>Test & Optimize</h3>
              <p>Continuously test subject lines, content, and timing to maximize performance.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="email-benefits">
        <div className="container">
          <div className="section-header">
            <div className="label">WHY EMAIL MARKETING</div>
            <h2>Why Email Marketing Works</h2>
          </div>

          <div className="benefits-grid">
            <div className="benefit-item">
              <div className="benefit-icon">💰</div>
              <h3>High ROI</h3>
              <p>Email marketing delivers an average ROI of $42 for every $1 spent, making it one of the most cost-effective marketing channels.</p>
            </div>

            <div className="benefit-item">
              <div className="benefit-icon">🎯</div>
              <h3>Direct Communication</h3>
              <p>Reach your audience directly in their inbox with personalized messages that build trust and drive action.</p>
            </div>

            <div className="benefit-item">
              <div className="benefit-icon">📈</div>
              <h3>Measurable Results</h3>
              <p>Track opens, clicks, conversions, and revenue with detailed analytics and reporting.</p>
            </div>

            <div className="benefit-item">
              <div className="benefit-icon">🤖</div>
              <h3>Automation</h3>
              <p>Set up automated sequences that nurture leads and engage customers without manual effort.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="email-cta">
        <div className="container">
          <div className="email-cta-content">
            <h2>Ready to Grow Your Email List?</h2>
            <p>Let's create email campaigns that engage your audience and drive real business results.</p>
            <Link to="/contact" className="btn btn-primary">
              Start Your Email Campaign
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EmailMarketing;
